import React from 'react';

import { Hourglass } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SidebarMenuButton } from '@/components/ui/sidebar';
import { useFeatureFlags } from '@/modules/common/hooks';
import { useDemoStatus } from '@/modules/demo/hooks';

interface DemoTrialBarProps {
  isCollapsed?: boolean;
}

/**
 * Trial-status row rendered in the sidebar footer between the onboarding
 * guide and the notifications button.
 *
 * Expanded: full-width amber pill with hourglass icon and trial label.
 * Collapsed: standard SidebarMenuButton (matches sibling icons like
 * Notifications/Settings) with the hourglass icon highlighted in the demo
 * accent colour and a tooltip showing the full trial text.
 */
export const DemoTrialBar: React.FC<DemoTrialBarProps> = ({ isCollapsed = false }) => {
  const { t } = useTranslation('common');
  const { isDemoEnv } = useFeatureFlags();
  const { daysLeft, error } = useDemoStatus();

  if (!isDemoEnv) {
    return null;
  }

  // When the trial-status endpoint fails or has not yet returned a value, fall
  // back to a generic invitation. Once the API returns a day count, swap to
  // the precise countdown.
  const hasDayCount = !error && daysLeft !== undefined;

  const buildLabel = (): string => {
    if (!hasDayCount) return t('demo.exploreTrial');
    if (daysLeft === 0) return `${t('demo.trialLabel')} ${t('demo.trialEnded')}`;
    const days =
      daysLeft === 1
        ? t('demo.daysLeftOne', { count: daysLeft })
        : t('demo.daysLeftMany', { count: daysLeft });
    return `${t('demo.trialLabel')} ${days}`;
  };

  const label = buildLabel();

  if (isCollapsed) {
    return (
      <SidebarMenuButton
        tooltip={label}
        className="bg-demo-banner text-demo-banner-foreground hover:bg-demo-banner hover:text-demo-banner-foreground"
      >
        <Hourglass className="h-4 w-4" />
      </SidebarMenuButton>
    );
  }

  return (
    <div className="flex items-center gap-2 rounded-md bg-demo-banner px-3 py-4 text-sm font-medium text-demo-banner-foreground">
      <Hourglass className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
};
